import { Alert, Box, Tab, Tabs, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { PackingOperation } from '../schema';
import { getTrailerItemTypesAtEvent } from '../utils/taskDisplayHelpers';
import ConfirmationChecklist from './ConfirmationChecklist';
import TabPanel from './TabPanel';

interface Props {
    operation: PackingOperation;
}

export default function FinalConfirmationTab({ operation }: Props) {
    const targetEvents = useMemo(
        () => (operation.targetEvents || []) as PackingOperation['targetEvents'][number][],
        [operation.targetEvents]
    );
    const [selectedEventIndex, setSelectedEventIndex] = useState(0);

    // Use clamped index for display when targetEvents shrinks (avoid setState-in-effect)
    const safeSelectedEventIndex = targetEvents.length > 0 ? Math.min(selectedEventIndex, targetEvents.length - 1) : 0;

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setSelectedEventIndex(newValue);
    };

    // Calculate overall progress
    const overallProgress = useMemo(() => {
        let totalItems = 0;
        let confirmedItems = 0;

        for (const event of targetEvents) {
            const packingListItems = (event.packingListItems || []) as any[];
            for (const item of packingListItems) {
                totalItems++;
                if (item.confirmed) {
                    confirmedItems++;
                }
            }
        }

        return { confirmed: confirmedItems, total: totalItems };
    }, [targetEvents]);

    if (targetEvents.length === 0) {
        return (
            <Box>
                <Alert severity="info" sx={{ mb: 3 }}>
                    This operation is unpacking items to HQ. There are no target events to confirm.
                </Alert>
            </Box>
        );
    }

    // Single target event - show simple checklist
    if (targetEvents.length === 1) {
        const event = targetEvents[0];
        return (
            <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
                <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2" component="span">
                        This checklist is <strong>for your confirmation only</strong>—it does not record item moves or update inventory. Use
                        it to verify items before departing; task checkboxes on the Tasks tab are what actually record moves.
                    </Typography>
                </Alert>

                {overallProgress.total > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            {overallProgress.confirmed}/{overallProgress.total} items confirmed
                        </Typography>
                    </Box>
                )}

                {(() => {
                    const trailers = getTrailerItemTypesAtEvent(operation, event.id);
                    return trailers.length > 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Trailers at this stop: {trailers.map(t => t.name || 'Trailer').join(', ')}
                        </Typography>
                    ) : null;
                })()}

                <ConfirmationChecklist event={event} operation={operation} isReadOnly={operation.status === 'completed'} />
            </Box>
        );
    }

    // Multiple target events - show tabs
    return (
        <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
            <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2" component="span">
                    This checklist is <strong>for your confirmation only</strong>—it does not record item moves or update inventory. Use it
                    to verify items before departing; task checkboxes on the Tasks tab are what actually record moves.
                </Typography>
            </Alert>

            {overallProgress.total > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {overallProgress.confirmed}/{overallProgress.total} items confirmed across all events
                </Typography>
            )}

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, minWidth: 0 }}>
                <Tabs
                    value={safeSelectedEventIndex}
                    onChange={handleTabChange}
                    aria-label="target event tabs"
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                >
                    {targetEvents.map((event, index) => {
                        const packingListItems = (event.packingListItems || []) as any[];
                        const confirmedCount = packingListItems.filter(item => item.confirmed).length;
                        const totalCount = packingListItems.length;
                        const percentComplete = totalCount > 0 ? Math.round((confirmedCount / totalCount) * 100) : 0;

                        return (
                            <Tab
                                key={event.id}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        <span>{event.name}</span>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color:
                                                    percentComplete === 100
                                                        ? 'success.main'
                                                        : percentComplete > 0
                                                          ? 'warning.main'
                                                          : 'text.secondary',
                                            }}
                                        >
                                            ({confirmedCount}/{totalCount})
                                        </Typography>
                                    </Box>
                                }
                                id={`confirmation-tab-${index}`}
                                aria-controls={`confirmation-tabpanel-${index}`}
                            />
                        );
                    })}
                </Tabs>
            </Box>

            {targetEvents.map((event, index) => {
                const trailers = getTrailerItemTypesAtEvent(operation, event.id);
                return (
                    <TabPanel key={event.id} value={safeSelectedEventIndex} index={index} idPrefix="confirmation" contentSx={{ py: 2 }}>
                        {trailers.length > 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Trailers at this stop: {trailers.map(t => t.name || 'Trailer').join(', ')}
                            </Typography>
                        )}
                        <ConfirmationChecklist event={event} operation={operation} isReadOnly={operation.status === 'completed'} />
                    </TabPanel>
                );
            })}
        </Box>
    );
}
